import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { getFiservTokenizerConfigService } from '../../services/paymentService';

const FISERV_TOKENIZER_URLS = {
    production: 'https://isv.cardconnect.com/itoke/ajax-tokenizer.html',
    uat: 'https://isv-uat.cardconnect.com/itoke/ajax-tokenizer.html',
};

// Card number, expiration and CVV are all captured inside the iframe; CardSecure binds the
// expiration and CVV to the token for 2 minutes or until its first use, so none of them reach our page or server.
// orientation=custom drops the tokenizer's own <br> layout so the css below controls it.
// enhancedresponse puts a stable errorCode on every message, so a CardSecure failure cannot be mistaken for a token.
const FISERV_TOKENIZER_PARAMS =
    'cardinputmaxlength=16&cardnumbernumericonly=true&placeholder=Card Number*' +
    '&useexpiry=true&useexpiryfield=true&placeholdermonth=MM&placeholderyear=YYYY' +
    '&usecvv=true&placeholdercvv=CVV' + // month, year and CVV placeholders are dropped unless they match [0-9a-zA-Z\s.#]
    '&invalidcreditcardevent=true&invalidexpiryevent=true&invalidcvvevent=true&enhancedresponse=true' +
    '&orientation=custom&sendcssloadedevent=true';

// The tokenizer re-validates every field on each blur and posts one message per outstanding problem,
// so "... is Required" arrives long before the user has reached that field. Only the invalid codes
// describe a field they have finished with; the required codes just mean "not filled in yet".
const INVALID_FIELD_MESSAGES = {
    1001: 'Invalid card number',
    1002: 'Invalid CVV',
    1003: 'Invalid card expiration',
};

const REQUIRED_FIELD_LABELS = [
    ['1004', 'card number'],
    ['1006', 'card expiration'],
    ['1005', 'CVV'],
];

// One blur produces a burst of messages; let it settle before deciding what to show
const VALIDATION_ROUND_MS = 50;

// CardSecure keeps the expiration and CVV for at most 2 minutes, after which an auth on the token is declined
const TOKEN_LIFETIME_MS = 110 * 1000;
const TOKEN_EXPIRED_MESSAGE =
    'For your security, card details are cleared after a few minutes. Please re-enter your card.';

// Upper bound on waiting for the tokenizer to report on the field a Pay click just left
const TOKEN_WAIT_MS = 5000;
const CARD_INCOMPLETE_MESSAGE = 'Please complete the card number, expiration and CVV fields.';
const CARD_UNVERIFIED_MESSAGE = 'We could not verify your card details. Please check them and try again.';

const listFields = (labels) =>
    labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0];

// Two rows inside the iframe: card number, then expiration + CVV. The page's own inputs use placeholders
// rather than labels, so the tokenizer's labels are hidden and the CVV field is pulled up beside the expiration.
const FIELD_HEIGHT = 42; // 28px line-height + 6px padding * 2 + 1px border * 2, as .payment-form-input
const ROW_GAP = 4;
const FISERV_IFRAME_HEIGHT = `${FIELD_HEIGHT * 2 + ROW_GAP}px`;

// Second row, as percentages of the iframe width: MM / YYYY, a gap, then CVV
const MONTH_WIDTH = 30;
const SLASH_WIDTH = 4;
const YEAR_WIDTH = 30;
const EXPIRY_WIDTH = MONTH_WIDTH + SLASH_WIDTH + YEAR_WIDTH;
const COLUMN_GAP = 2;
const CVV_WIDTH = 100 - EXPIRY_WIDTH - COLUMN_GAP;

const INPUT_TEXT = '#495057';
const INPUT_BORDER = '#ced4da';
const INPUT_FOCUS_BORDER = '#80bdff';
const INPUT_PLACEHOLDER = '#6c757d';
const DANGER = '#dc3545';

const SKELETON_FIELD_STYLE = {
    height: `${FIELD_HEIGHT}px`,
    padding: '6px 0.75rem',
    fontSize: '18px',
    lineHeight: '28px',
    color: INPUT_PLACEHOLDER,
    backgroundColor: '#e9ecef',
    border: `1px solid ${INPUT_BORDER}`,
    borderRadius: '5px',
    boxSizing: 'border-box',
    overflow: 'hidden',
};

const FiservCardNumberField = (
    {
        onTokenReceived = () => {},
        onErrorReceived = () => {},
        companyID = null,
        panelColor = '#383636',
        textColor = '#ffffff',
        showSecurelyCapturedMessage = false,
        errorMessage = null,
    },
    ref
) => {
    const [fiservToken, setFiservToken] = useState('');
    const [iframeLoading, setIframeLoading] = useState(true);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [cssLoaded, setCssLoaded] = useState(false);
    const [tokenizerIsDev, setTokenizerIsDev] = useState(null);
    const [configError, setConfigError] = useState(false);
    const iframeRef = useRef(null);
    const validationRoundRef = useRef([]);
    const validationFlushRef = useRef(null);
    // Read by getToken, which a submit handler calls from a render that predates the token
    const tokenRef = useRef('');
    const tokenWaitersRef = useRef([]);
    const lastMessageRef = useRef(null);
    const reportedAtRef = useRef(0);
    const focusLeftAtRef = useRef(0);
    const iframeFocusedRef = useRef(false);

    const resetValidationRound = () => {
        clearTimeout(validationFlushRef.current);
        validationRoundRef.current = [];
    };

    const storeToken = (token) => {
        tokenRef.current = token;
        setFiservToken(token);
        onTokenReceived(token);
    };

    // Every blur out of a card field ends in exactly one report: a token, a CardSecure error or a validation round
    const report = (token, message = null) => {
        const result = { token, message: token ? null : message || CARD_INCOMPLETE_MESSAGE };
        reportedAtRef.current = Date.now();
        lastMessageRef.current = result.message;
        const waiters = tokenWaitersRef.current;
        tokenWaitersRef.current = [];
        waiters.forEach((settle) => settle(result));
    };

    useImperativeHandle(ref, () => ({
        // Resolves to { token } or, when there is none, { token: '', message } explaining why
        getToken: () => {
            if (tokenRef.current) return Promise.resolve({ token: tokenRef.current, message: null });
            if (focusLeftAtRef.current <= reportedAtRef.current) {
                return Promise.resolve({ token: '', message: lastMessageRef.current || CARD_INCOMPLETE_MESSAGE });
            }
            return new Promise((resolve) => {
                const timer = setTimeout(() => report('', CARD_UNVERIFIED_MESSAGE), TOKEN_WAIT_MS);
                tokenWaitersRef.current.push((result) => {
                    clearTimeout(timer);
                    resolve(result);
                });
            });
        },
    }));

    useEffect(
        () => () => {
            clearTimeout(validationFlushRef.current);
            report('');
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        const iframeIsFocused = () => !!iframeRef.current && document.activeElement === iframeRef.current;
        const markFocusLeft = () => {
            focusLeftAtRef.current = Date.now();
            iframeFocusedRef.current = false;
        };
        // This page's window blurs when focus moves into the iframe
        const handleWindowBlur = () =>
            setTimeout(() => {
                iframeFocusedRef.current = iframeIsFocused();
            }, 0);
        // A press anywhere on the page takes focus out of the iframe before its click lands
        const handlePointerDown = () => {
            if (iframeIsFocused()) markFocusLeft();
        };
        // Tabbing out of the iframe lands on a page element
        const handleFocusIn = () => {
            if (iframeFocusedRef.current) markFocusLeft();
        };

        window.addEventListener('blur', handleWindowBlur);
        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('focusin', handleFocusIn, true);
        return () => {
            window.removeEventListener('blur', handleWindowBlur);
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('focusin', handleFocusIn, true);
        };
    }, []);

    // The tokenizer page is cross-origin, so its styling travels in the URL. Built once: a new value would reload the iframe.
    // CardPointe's sanitizer allows no float, flex, grid or position: the columns are inline-block widths
    const [fiservIframeCss] = useState(() => ({
        body: {
            margin: '0',
            padding: '0',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            fontSize: '18px',
            background: panelColor,
        },
        // The only bare label is the "/" between month and year
        label: {
            display: 'inline-block',
            width: `${SLASH_WIDTH}%`,
            margin: '0',
            textAlign: 'center',
            lineHeight: `${FIELD_HEIGHT}px`,
            verticalAlign: 'top',
            color: textColor,
        },
        '#cccardlabel, #ccexpirylabel, #cccvvlabel': { display: 'none' },
        input: {
            display: 'block',
            width: '100%',
            margin: '0',
            padding: '6px 0.75rem',
            fontSize: '18px',
            fontFamily: 'inherit',
            fontWeight: '400',
            lineHeight: '28px',
            color: INPUT_TEXT,
            backgroundColor: '#ffffff',
            border: `1px solid ${INPUT_BORDER}`,
            borderRadius: '5px',
            boxSizing: 'border-box',
        },
        '#ccnumfield': { marginBottom: `${ROW_GAP}px` },
        '#ccexpiryfieldmonth': { display: 'inline-block', width: `${MONTH_WIDTH}%`, verticalAlign: 'top' },
        '#ccexpiryfieldyear': { display: 'inline-block', width: `${YEAR_WIDTH}%`, verticalAlign: 'top' },
        '#cccvvfield': {
            width: `${CVV_WIDTH}%`,
            marginLeft: `${EXPIRY_WIDTH + COLUMN_GAP}%`,
            marginTop: `-${FIELD_HEIGHT}px`,
        },
        'input::placeholder': { color: INPUT_PLACEHOLDER, fontStyle: 'italic' },
        'input:focus': { borderColor: INPUT_FOCUS_BORDER, outline: '0' },
    }));

    const cssObjectToUrlEncoded = (cssObj) => {
        const cssString = Object.entries(cssObj)
            .map(([selector, styles]) => {
                const styleString = Object.entries(styles)
                    .map(([prop, value]) => `${prop.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}:${value}`)
                    .join(';');
                return `${selector}{${styleString}}`;
            })
            .join('');
        return encodeURIComponent(cssString);
    };

    // The backend decides which Fiserv site this company uses; the iframe must load from the same site or the token is rejected
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await getFiservTokenizerConfigService(companyID);
                if (!active) return;
                if (typeof data?.isDev !== 'boolean') throw new Error('Fiserv tokenizer config missing isDev');
                setTokenizerIsDev(data.isDev);
            } catch (error) {
                if (!active) return;
                console.error('Failed to load Fiserv tokenizer config:', error);
                setConfigError(true);
                setIframeLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [companyID]);

    const fiservIframeUrl =
        tokenizerIsDev === null
            ? null
            : `${tokenizerIsDev ? FISERV_TOKENIZER_URLS.uat : FISERV_TOKENIZER_URLS.production}?${FISERV_TOKENIZER_PARAMS}` +
              `&css=${cssObjectToUrlEncoded(fiservIframeCss)}`;

    useEffect(() => {
        // A round re-reports every outstanding problem, so it is the complete picture: anything it
        // does not mention is now fine, which is the only way a cleared field stops being an error.
        const flushValidationRound = () => {
            const codes = validationRoundRef.current;
            validationRoundRef.current = [];

            const invalidCode = codes.find((code) => INVALID_FIELD_MESSAGES[code]);
            const invalidMessage = invalidCode ? INVALID_FIELD_MESSAGES[invalidCode] : null;
            onErrorReceived(invalidMessage);

            const missing = REQUIRED_FIELD_LABELS.filter(([code]) => codes.includes(code)).map(([, label]) => label);
            report('', missing.length ? `Please enter the ${listFields(missing)}.` : invalidMessage);
        };

        const handleFiservMessage = (event) => {
            try {
                const url = new URL(event.origin);
                if (url.protocol !== 'https:' || !url.hostname.endsWith('.cardconnect.com')) return;

                const tokenData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (tokenData.cssLoaded) {
                    setCssLoaded(true);
                    return;
                }

                if (tokenData.validationError) {
                    validationRoundRef.current.push(String(tokenData.errorCode));
                    clearTimeout(validationFlushRef.current);
                    validationFlushRef.current = setTimeout(flushValidationRound, VALIDATION_ROUND_MS);
                    return;
                }

                // CardSecure rejected the card itself: a token-shaped message carrying its own code
                if (tokenData.errorCode && tokenData.errorCode !== '0') {
                    const cardError = tokenData.errorMessage || 'Card could not be verified';
                    storeToken('');
                    onErrorReceived(cardError);
                    report('', cardError);
                    return;
                }

                // An empty token means the tokenizer wiped its own fields because the user started editing again
                if ('token' in tokenData || 'message' in tokenData) {
                    const token = tokenData.token ?? tokenData.message ?? '';
                    storeToken(token);
                    if (token) {
                        resetValidationRound();
                        onErrorReceived(null);
                        report(token);
                    }
                }
            } catch (error) {
                console.error('Error parsing Fiserv message:', error);
            }
        };

        window.addEventListener('message', handleFiservMessage, false);
        return () => window.removeEventListener('message', handleFiservMessage, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onTokenReceived, onErrorReceived]);

    // The tokenizer fetches our stylesheet through its sanitizer, which can land after the iframe's load event,
    // so wait for both before revealing it. Fall back to a timer if it never reports the stylesheet.
    useEffect(() => {
        if (!iframeLoaded) return undefined;
        if (cssLoaded) {
            setIframeLoading(false);
            return undefined;
        }
        const timer = setTimeout(() => setIframeLoading(false), 1500);
        return () => clearTimeout(timer);
    }, [iframeLoaded, cssLoaded]);

    const clearToken = (message = null) => {
        storeToken('');
        resetValidationRound();
        onErrorReceived(message);
        report('', message);
        if (iframeRef.current) {
            iframeRef.current.src = iframeRef.current.src;
            setIframeLoading(true);
            setIframeLoaded(false);
            setCssLoaded(false);
        }
    };

    // The tokenizer re-posts the same token on every blur, which leaves this state unchanged and so does not restart the clock
    useEffect(() => {
        if (!fiservToken) return undefined;
        const timer = setTimeout(() => clearToken(TOKEN_EXPIRED_MESSAGE), TOKEN_LIFETIME_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fiservToken]);

    return (
        <div className="mb-3">
            <div style={{ position: 'relative' }}>
                {fiservToken && (
                    <button
                        type="button"
                        onClick={() => clearToken()}
                        style={{
                            position: 'absolute',
                            top: `${FIELD_HEIGHT / 2}px`,
                            right: '10px',
                            transform: 'translateY(-50%)',
                            zIndex: 20,
                            background: 'transparent',
                            border: 'none',
                            color: INPUT_PLACEHOLDER,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px',
                        }}
                        title="Clear card details"
                    >
                        ✕
                    </button>
                )}
                {/* Placeholder while iframe loads, laid out like the fields it stands in for */}
                {iframeLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: FISERV_IFRAME_HEIGHT,
                            backgroundColor: panelColor,
                            zIndex: 10,
                        }}
                    >
                        <div style={{ ...SKELETON_FIELD_STYLE, marginBottom: `${ROW_GAP}px` }}>Card Number*</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ ...SKELETON_FIELD_STYLE, width: `${EXPIRY_WIDTH}%` }}>MM / YYYY</div>
                            <div style={{ ...SKELETON_FIELD_STYLE, width: `${CVV_WIDTH}%` }}>CVV</div>
                        </div>
                    </div>
                )}
                {configError ? (
                    <div style={{ color: DANGER, padding: '12px 0' }}>
                        <i className="fas fa-exclamation-triangle mr-1" /> Unable to load the secure card field. Please
                        refresh the page and try again.
                    </div>
                ) : (
                    fiservIframeUrl && (
                        <iframe
                            ref={iframeRef}
                            id="tokenFrame"
                            name="tokenFrame"
                            src={fiservIframeUrl}
                            style={{
                                width: '100%',
                                height: FISERV_IFRAME_HEIGHT,
                                border: 'none',
                                display: 'block',
                                opacity: iframeLoading ? 0 : 1,
                                transition: 'opacity 0.2s ease-in-out',
                            }}
                            scrolling="no"
                            title="Fiserv Card Tokenizer"
                            onLoad={() => setIframeLoaded(true)}
                            onError={() => {
                                console.error('Fiserv iframe failed to load');
                                setIframeLoading(false);
                            }}
                        />
                    )
                )}
            </div>
            {/* Always rendered at a fixed height so a message appearing never pushes the form down */}
            <small
                className="d-block mt-1"
                title={errorMessage || undefined}
                style={{
                    height: '1.5em',
                    lineHeight: '1.5em',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color: errorMessage ? DANGER : textColor,
                }}
            >
                {errorMessage ? (
                    <>
                        <i className="fas fa-exclamation-triangle mr-1" /> {errorMessage}
                    </>
                ) : (
                    showSecurelyCapturedMessage && (
                        <>
                            <i className="fas fa-lock mr-1" /> Card information securely captured
                        </>
                    )
                )}
            </small>
        </div>
    );
};

export default forwardRef(FiservCardNumberField);
